import React, { useEffect, useState } from "react";
import "./Packages.css";
import { FaCheckCircle } from "react-icons/fa";
import { supabase } from "../supabaseClient";
import { useAuth } from "../contexts/SupabaseAuthProvider";

export default function Packages() {
  const { user } = useAuth();

  const [notif, setNotif] = useState(null);
  const [balance, setBalance] = useState(0);
  const [active, setActive] = useState(null); // active_package JSONB
  const [totalInvested, setTotalInvested] = useState(0);

  // ───────────────────────────────────
  // LOAD FROM SUPABASE (now includes total_invested)
  // ───────────────────────────────────
  const loadData = async () => {
    if (!user?.id) return;

    const { data, error } = await supabase
      .from("users_data")
      .select("balance, user_package, active_package, total_invested")
      .eq("user_id", user.id)
      .single();

    if (error) return console.log("Load package error:", error);

    setBalance(Number(data.balance || 0));
    setActive(data.active_package || {});
    setTotalInvested(Number(data.total_invested || 0));
  };

  useEffect(() => {
    loadData();
  }, [user?.id]);

  // ───────────────────────────────────
  // REALTIME UPDATE (also updates total_invested)
  // ───────────────────────────────────
  useEffect(() => {
    if (!user?.id) return;

    const ch = supabase
      .channel("pkg_realtime")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "users_data",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          setBalance(Number(payload.new?.balance || 0));
          setActive(payload.new?.active_package || {});
          setTotalInvested(Number(payload.new?.total_invested || 0));
        }
      )
      .subscribe();

    return () => supabase.removeChannel(ch);
  }, [user?.id]);

  // ───────────────────────────────────
  // PACKAGE LIST
  // ───────────────────────────────────
  const packages = [
    { price: 30, puzzles: 1, daily: 2 },
    { price: 50, puzzles: 2, daily: 4 },
    { price: 100, puzzles: 5, daily: 10 },
    { price: 200, puzzles: 15, daily: 30 },
    { price: 300, puzzles: 20, daily: 40 },
    { price: 500, puzzles: 35, daily: 70 },
    { price: 1000, puzzles: 60, daily: 120 },
    { price: 3000, puzzles: 150, daily: 300 },
    { price: 5000, puzzles: 350, daily: 700 },
  ];

  // ───────────────────────────────────
  // ACTIVATE PACKAGE (now updates total_invested)
  // ───────────────────────────────────
  const activatePackage = async (pkg) => {
    if (balance < pkg.price) {
      setNotif({ type: "error", text: "Insufficient Funds! Please deposit." });
      return;
    }

    // calculate new values
    const now = new Date().toISOString();
    const expiry = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();

    const pkgData = {
      price: pkg.price,
      puzzles: pkg.puzzles,
      daily: pkg.daily,
      activatedAt: now,
      expiresAt: expiry,
    };

    const newBalance = balance - pkg.price;
    const newTotalInvested = (totalInvested || 0) + pkg.price;

    // update all relevant fields in one query
    const { error } = await supabase
      .from("users_data")
      .update({
        balance: newBalance,
        user_package: pkg.price,
        last_package_date: now,
        next_profit_time: expiry,
        active_package: pkgData,
        total_invested: newTotalInvested,
        updated_at: now,
      })
      .eq("user_id", user.id);

    if (error) {
      console.log("Activate error:", error);
      setNotif({ type: "error", text: "Activation failed. Try again." });
      return;
    }

    // local state sync
    setNotif({
      type: "success",
      text: `Package $${pkg.price} Activated Successfully!`,
    });

    setBalance(newBalance);
    setActive(pkgData);
    setTotalInvested(newTotalInvested);
  };

  // ───────────────────────────────────
  // DEACTIVATE PACKAGE
  // ───────────────────────────────────
  const deactivatePackage = () => {
    if (!active?.price) return;

    setNotif({
      type: "warning",
      text: "Deactivate package? 20% penalty will be deducted.",
      action: async () => {
        const penalty = active.price * 0.2;
        const refund = active.price - penalty;

        const newBalance = balance + refund;

        const { error } = await supabase
          .from("users_data")
          .update({
            balance: newBalance,
            user_package: 0,
            last_package_date: null,
            next_profit_time: null,
            active_package: {},
            updated_at: new Date().toISOString(),
            // note: total_invested is NOT decreased on deactivate — keep history intact
          })
          .eq("user_id", user.id);

        if (error) {
          console.log("Deactivate error:", error);
          setNotif({ type: "error", text: "Deactivate failed." });
          return;
        }

        setBalance(newBalance);
        setActive(null);
        setNotif({
          type: "error",
          text: `Package Deactivated — 20% Penalty Deducted`,
        });
      },
    });
  };

  // ───────────────────────────────────
  // AUTO-EXPIRE PACKAGE
  // ───────────────────────────────────
  useEffect(() => {
    if (!active?.expiresAt) return;

    if (Date.now() >= new Date(active.expiresAt).getTime()) {
      (async () => {
        const { error } = await supabase
          .from("users_data")
          .update({
            user_package: 0,
            last_package_date: null,
            next_profit_time: null,
            active_package: {},
          })
          .eq("user_id", user.id);

        if (error) {
          console.log("Expire update error:", error);
        }

        setActive(null);
        setNotif({
          type: "error",
          text: "Your Package Has Expired (30 Days Completed)",
        });
      })();
    }
  }, [active]);

  const activePrice = active?.price || 0;

  return (
    <div className="packages-page">
      <h2 className="heading">Premium Packages</h2>

      {/* ACTIVE PACKAGE BANNER */}
      {activePrice !== 0 && (
        <div className="active-banner">
          <FaCheckCircle className="tick-icon" />
          <span>Active Package: ${activePrice}</span>

          <button className="deactivate-btn" onClick={deactivatePackage}>
            Deactivate (20% Fee)
          </button>
        </div>
      )}

      {/* PACKAGES GRID */}
      <div className="packages-grid">
        {packages.map((pkg, i) => {
          const isActive = activePrice === pkg.price;

          return (
            <div
              className={`package-card ${isActive ? "active-card" : ""}`}
              key={i}
            >
              {isActive && (
                <div className="activated-badge">
                  <FaCheckCircle className="tick-icon" /> Activated
                </div>
              )}

              <h3 className="package-title">${pkg.price} Premium</h3>

              <div className="details">
                <p>
                  Daily Puzzles: <strong>{pkg.puzzles}</strong>
                </p>
                <p>
                  Daily Earnings: <strong>${pkg.daily}</strong>
                </p>
              </div>

              <button
                className={`activate-btn ${isActive ? "active-btn" : ""}`}
                onClick={() => activatePackage(pkg)}
                disabled={isActive}
              >
                {isActive ? "Activated" : "Activate"}
              </button>
            </div>
          );
        })}
      </div>

      {/* SIMPLE NOTIF */}
      {notif && !notif.action && (
        <div
          className={`package-notif ${
            notif.type === "error"
              ? "error"
              : notif.type === "warning"
              ? "warning"
              : "success"
          }`}
        >
          {notif.text}
        </div>
      )}

      {/* MODAL */}
      {notif?.type === "warning" && notif.action && (
        <div className="premium-overlay">
          <div className="premium-modal">
            <h3 className="pm-title">Confirm Deactivation</h3>
            <p className="pm-msg">{notif.text}</p>

            <div className="pm-buttons">
              <button className="pm-cancel" onClick={() => setNotif(null)}>
                Cancel
              </button>

              <button
                className="pm-confirm"
                onClick={() => {
                  notif.action();
                  setNotif(null);
                }}
              >
                Yes, Deactivate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
